// Native touch scrolling, keyboard access and bounded desktop dragging.
class PinduProductCarousel extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    const track = this.querySelector('[data-pindu-track]');
    if (!track) return;
    const previous = this.querySelector('[aria-label="Previous products"]');
    const next = this.querySelector('[aria-label="Next products"]');
    track.addEventListener('click', event => {
      const swatch = event.target.closest('.pindu-watch-swatch');
      if (!swatch || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const card = swatch.closest('[class*="product-card-"], [class*="__card-"]');
      const image = card?.querySelector('a[class*="image-wrapper-"] img');
      if (!image) return;
      event.preventDefault();
      image.src = swatch.dataset.watchImage;
      image.removeAttribute('srcset');
      image.alt = swatch.dataset.watchName;
      card.querySelectorAll('a:not(.pindu-watch-swatch)').forEach(link => { link.href = swatch.href; });
      card.querySelectorAll('.pindu-watch-swatch').forEach(link => {
        if (link === swatch) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }, options);
    this.classList.remove('pindu-products--grid');
    this.querySelector('.pindu-carousel-status')?.remove();
    const update = () => {
      if (previous) previous.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      this.classList.toggle('pindu-products--overflow', track.scrollWidth > track.clientWidth + 2);
    };
    const move = direction => {
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = (track.firstElementChild?.getBoundingClientRect().width || track.clientWidth) + gap;
      const count = matchMedia('(min-width: 1024px)').matches ? Math.max(1, Math.round((track.clientWidth + gap) / step)) : 1;
      track.scrollBy({ left: direction * step * count, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
    previous?.addEventListener('click', () => move(-1), options);
    next?.addEventListener('click', () => move(1), options);
    track.addEventListener('scroll', update, { ...options, passive: true });
    track.addEventListener('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      move(event.key === 'ArrowRight' ? 1 : -1);
    }, options);
    let start, dragged = false;
    track.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      start = { x: event.clientX, scroll: track.scrollLeft };
      dragged = false;
    }, options);
    window.addEventListener('pointermove', event => {
      if (!start) return;
      const distance = event.clientX - start.x;
      if (Math.abs(distance) > 6) dragged = true;
      if (!dragged) return;
      track.classList.add('dragging');
      track.scrollLeft = start.scroll - distance;
    }, options);
    const finish = () => {
      start = null;
      track.classList.remove('dragging');
      // The click following pointerup must be suppressed, but later taps must work.
      setTimeout(() => { dragged = false; }, 0);
    };
    window.addEventListener('pointerup', finish, options);
    window.addEventListener('pointercancel', finish, options);
    track.addEventListener('dragstart', event => event.preventDefault(), options);
    track.addEventListener('click', event => { if (dragged) { event.preventDefault(); dragged = false; } }, { ...options, capture: true });
    this.resizeObserver = new ResizeObserver(update);
    this.resizeObserver.observe(track);
    update();
  }
  disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); }
}
if (!customElements.get('pindu-product-carousel')) customElements.define('pindu-product-carousel', PinduProductCarousel);
